/**
 * CSS Custom Properties Generation
 * 
 * Utilities for converting Leonardo Theme objects into CSS custom properties
 */

import { Theme } from '@adobe/leonardo-contrast-colors';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Color object from Leonardo with rounded contrast
 */
interface ColorValue {
  name: string;
  step: number;
  value: string;
  cssVarName: string;
}

// =============================================================================
// NAMING UTILITIES
// =============================================================================

/**
 * Convert any string to kebab-case for CSS variable names
 * This is a robust, predictable transformation that works for ANY input
 * 
 * @example
 * toKebabCase("Black") → "black"
 * toKebabCase("Vivid Orange") → "vivid-orange"
 * toKebabCase("LightVividRed") → "light-vivid-red"
 * toKebabCase("My Custom Color 123") → "my-custom-color-123"
 */
export const toKebabCase = (str: string): string => {
  return str
    .trim()
    // Insert hyphen before uppercase letters (for camelCase/PascalCase)
    .replace(/([A-Z])/g, '-$1')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove any non-alphanumeric characters except hyphens
    .replace(/[^a-z0-9-]/gi, '')
    // Convert to lowercase
    .toLowerCase()
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Collapse multiple consecutive hyphens
    .replace(/-+/g, '-');
};

/**
 * Generate CSS variable name from color name and step
 * Uses consistent kebab-case naming for predictable, sustainable output
 * 
 * Leonardo outputs color keys with the step number directly (e.g., "Black100", "Gray200")
 * We preserve this numbering as-is for CSS variable names.
 * 
 * @example
 * generateCSSVariableName("Black", 100) → "--color-black-100"
 * generateCSSVariableName("Vivid Orange", 300) → "--color-vivid-orange-300"
 */
export const generateCSSVariableName = (colorName: string, step: number): string => {
  const kebabName = toKebabCase(colorName);
  // Leonardo already outputs 100-based step numbers (100, 200, ..., 1000)
  // We use them directly without modification
  return `--color-${kebabName}-${step}`;
};

// =============================================================================
// COLOR EXTRACTION
// =============================================================================

/**
 * Extract colors from Leonardo theme
 * Leonardo returns colors as flat key-value pairs like:
 * { "Black100": "#ebeaec", "Red200": "#ff4433", ... }
 * 
 * Note: Leonardo typically appends numbers based on the list of colors provided.
 * If we provide 10 ratios, it usually outputs Name1...Name10 or Name + Ratio.
 * Our regex handles keys ending in numbers.
 */
export const extractColors = (theme: Theme, normalizeWhiteToBlack = false): ColorValue[] => {
  const colorPairs = theme.contrastColorPairs;
  const colors: ColorValue[] = [];
  
  Object.entries(colorPairs).forEach(([key, value]) => {
    // Skip background
    if (key === 'background') return;
    
    // Parse color name and step from key (e.g., "Black100" -> name: "Black", step: 100)
    // Note: If Leonardo outputs "Gray1", we parse step=1. 
    // generateCSSVariableName converts this to 100.
    const match = key.match(/^([A-Za-z\s]+?)(\d+)$/);
    if (match && match[1] && match[2]) {
      let name = match[1].trim();
      const stepStr = match[2];
      const step = parseInt(stepStr, 10);
      
      // CRITICAL FIX: Normalize "White" to "Black" for consistent CSS variable names
      // When backgroundColor is "White" (light mode), Leonardo outputs "White100", "White200", etc.
      // But we want consistent variable names (--color-black-*) regardless of mode
      // ONLY apply normalization for dynamic themes, NOT static themes
      if (normalizeWhiteToBlack && name === 'White') {
        name = 'Black';
      }
      
      colors.push({
        name,
        step,
        value: value as string,
        cssVarName: generateCSSVariableName(name, step),
      });
    }
  });
  
  return colors;
};

// =============================================================================
// CSS GENERATION
// =============================================================================

/**
 * Generate CSS custom properties object from Leonardo theme
 * 
 * @param theme - Leonardo theme
 * @param normalizeWhiteToBlack - If true, normalize "White" keys to "Black" for consistent dynamic theme variables
 * @returns Object like { "--color-black-100": "#ebeaec", ... }
 */
export const generateCSSVariables = (theme: Theme, normalizeWhiteToBlack = false): Record<string, string> => {
  const colors = extractColors(theme, normalizeWhiteToBlack);
  const cssVars: Record<string, string> = {};
  
  colors.forEach((color) => {
    cssVars[color.cssVarName] = color.value;
  });
  
  // Also add background color
  const bg = theme.contrastColorPairs.background;
  if (bg) {
    cssVars['--color-background'] = bg as string;
  }
  
  return cssVars;
};

/**
 * Generate STATIC CSS custom properties object from Leonardo theme
 * Prepends a prefix to variable names (e.g., "--color-light-black-100")
 * 
 * @param theme - Leonardo theme
 * @param prefix - Prefix to add (e.g., "light") -> results in "--color-light-..."
 */
export const generateStaticCSSVariables = (theme: Theme, prefix: string): Record<string, string> => {
  // Do NOT normalize for static themes - keep White/Black as-is for proper prefixing
  const baseVars = generateCSSVariables(theme, false);
  const staticVars: Record<string, string> = {};
  
  Object.entries(baseVars).forEach(([key, value]) => {
    if (key === '--color-background') {
      staticVars[`--color-${prefix}-background`] = value;
    } else {
      // key is like "--color-black-100"
      // replace "--color-" with "--color-{prefix}-"
      const newKey = key.replace('--color-', `--color-${prefix}-`);
      staticVars[newKey] = value;
    }
  });
  
  return staticVars;
};

/**
 * Generate CSS custom properties as a CSS string
 * 
 * @returns String like: "--color-red-389: #FF6038;\n--color-blue-120: #2E8CFF;"
 */
export const generateCSSString = (theme: Theme): string => {
  const cssVars = generateCSSVariables(theme);
  
  return Object.entries(cssVars)
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n  ');
};

/**
 * Inject CSS custom properties into the document root
 * This allows dynamic updates at runtime without rebuild
 * 
 * This is the key function for making colors reactive in the Hero demo
 * 
 * IMPORTANT: Base colors should ALWAYS be provided to ensure dynamic single-source behavior.
 * SCSS defaults in _foundation.scss are only used as fallback before JavaScript loads.
 * 
 * @param theme - Leonardo theme object
 * @param baseColors - Base colors to inject as --base-* variables (should always be provided)
 */
export const injectCSSVariables = (
  theme: Theme, 
  baseColors?: { black?: string; red?: string; orange?: string; blue?: string }
): void => {
  // Pass true to normalize White→Black for consistent dynamic theme variables
  const cssVars = generateCSSVariables(theme, true);
  const root = document.documentElement;
  
  Object.entries(cssVars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });

  // Inject base color CSS variables to ensure dynamic single-source behavior
  if (baseColors) {
    if (baseColors.black) root.style.setProperty('--base-black', baseColors.black);
    if (baseColors.red) root.style.setProperty('--base-red', baseColors.red);
    if (baseColors.orange) root.style.setProperty('--base-orange', baseColors.orange);
    if (baseColors.blue) root.style.setProperty('--base-blue', baseColors.blue);
  }
};

/**
 * Inject STATIC CSS custom properties into the document root
 * Used to update light/dark scales when base colors change
 */
export const injectStaticCSSVariables = (theme: Theme, prefix: string): void => {
  const cssVars = generateStaticCSSVariables(theme, prefix);
  const root = document.documentElement;
  
  Object.entries(cssVars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
};

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Get raw color pairs from Leonardo theme
 * Format: "Light Vivid Red_3.89" → "#FF6038"
 */
export const getColorPairs = (theme: Theme): Record<string, string> => {
  return theme.contrastColorPairs;
};

/**
 * Get all colors grouped by color name (kebab-case keys)
 * 
 * @returns { "black": [{ step: 100, value: "#..." }, ...], "vivid-orange": [...] }
 * 
 * Uses consistent kebab-case keys for predictable access patterns
 */
export const getColorsByName = (theme: Theme): Record<string, Array<{ step: number; value: string }>> => {
  const colors = extractColors(theme);
  const grouped: Record<string, Array<{ step: number; value: string }>> = {};
  
  colors.forEach((color) => {
    // Use the same kebab-case transformation for consistency
    const kebabName = toKebabCase(color.name);
    
    if (!grouped[kebabName]) {
      grouped[kebabName] = [];
    }
    
    grouped[kebabName].push({
      step: color.step,
      value: color.value,
    });
  });
  
  return grouped;
};
